import { DOMAINS } from "@/content";
import { ArrowRight, ArrowUpRight, Gauge, TrendingUp, Layers, Boxes, Zap, Shield } from "lucide-react";
import { Link } from "react-router-dom";

const iconFor = { fcf: Gauge, sales: TrendingUp, supply: Layers, mfg: Boxes };

const OFFERINGS = [
  "Business Growth & Go-to-Market Strategy",
  "Supply Chain & Manufacturing Transformation",
  "Operational Excellence (Lean, TOC & Productivity)",
  "Working Capital & Cash Flow Optimisation",
  "Industrial Aftermarket & Maintenance-as-a-Service",
  "Technology Commercialisation, Localisation & Vendor Development",
  "AI-enabled Manufacturing & Digital Operations",
  "CXO Advisory, Due Diligence, Performance Improvement",
];

const ENGAGEMENT_MODELS = [
  {
    name: "Diagnostic Sprint",
    duration: "2 weeks",
    price: "Fixed fee",
    for: "COO/CFO who want a quantified constraint map before committing capital.",
    includes: ["Constraint identification & quantification", "CCC + OEE + OTIF baseline", "30-90 day quick-win plan", "Trade-off register"],
    icon: Zap,
  },
  {
    name: "Transformation Programme",
    duration: "6-12 months",
    price: "Retainer + outcome-linked",
    for: "P&L leaders committing to a structural shift in cost, cash or capacity.",
    includes: ["Weekly plant + boardroom cadence", "Buffer & drum design", "Vendor + capacity restructuring", "OTIF & OEE recovery"],
    icon: Shield,
    featured: true,
  },
  {
    name: "CXO Advisory",
    duration: "Ongoing",
    price: "Monthly retainer",
    for: "Founder/CEO who wants a seasoned industrial operator on-call.",
    includes: ["Fortnightly review", "Board-pack sharpening", "Investor & due-diligence support", "Coaching for VP-level leaders"],
    icon: ArrowUpRight,
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      <section className="mx-auto max-w-[1440px] px-6 lg:px-10 pt-16 pb-14">
        <span className="eyebrow">Services</span>
        <h1 className="mt-4 text-4xl lg:text-6xl tracking-[-0.03em] font-semibold text-slate-900 leading-[1.02] max-w-4xl">
          Four domains. <span className="text-slate-500">One financial North Star.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-[17px] text-slate-600 leading-[1.7]">
          Every engagement is anchored to a single metric — free cash flow — and executed through the discipline of constraint theory.
          Revenue without cash velocity is treated as operational risk, not achievement.
        </p>
      </section>

      {/* 4 domains detailed */}
      <section className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {DOMAINS.map((d) => {
            const Icon = iconFor[d.key];
            return (
              <div key={d.key} data-testid={`service-${d.key}`} className="ind-card p-8">
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500">{d.number} · {d.kicker}</div>
                    <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">{d.title}</h3>
                  </div>
                  <div className="h-12 w-12 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
                <ul className="space-y-3 text-[15px] text-slate-600">
                  {d.bullets.map((b, i) => (
                    <li key={i} className="flex gap-3">
                      <span className={`mt-2 h-1.5 w-1.5 rounded-full shrink-0 ${d.dot}`} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between text-sm">
                  <span className="text-slate-500">Typical outcome window</span>
                  <span className="font-medium text-slate-900">30–180 days</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Offerings */}
      <section className="border-t border-slate-200 mt-20 bg-slate-50">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5">
              <span className="eyebrow">Consulting Offerings</span>
              <h2 className="mt-3 text-3xl lg:text-4xl tracking-tight font-semibold text-slate-900">
                The full toolkit of an industrial P&L operator.
              </h2>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-3">
              {OFFERINGS.map((o, i) => (
                <div key={i} data-testid={`offering-${i}`} className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-[#712ae2] shrink-0" />
                  <span className="text-[15px] text-slate-800">{o}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="mx-auto max-w-[1440px] px-6 lg:px-10 py-20">
        <div className="mb-10">
          <span className="eyebrow">Engagement Models</span>
          <h2 className="mt-3 text-3xl lg:text-5xl tracking-tight font-semibold text-slate-900">
            Three ways to work with Indusynq.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {ENGAGEMENT_MODELS.map((m, i) => {
            const Icon = m.icon;
            return (
              <div
                key={i}
                data-testid={`engagement-${i}`}
                className={`rounded-2xl p-7 relative overflow-hidden ${
                  m.featured
                    ? "bg-[#0F172A] text-white border border-[#1E293B]"
                    : "bg-white border border-slate-200"
                }`}
              >
                {m.featured && (
                  <span className="absolute right-5 top-5 text-[10px] tracking-[0.2em] uppercase text-[#A78BFA] font-semibold">Most engaged</span>
                )}
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${m.featured ? "bg-white/10 text-white" : "bg-slate-900 text-white"}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className={`mt-5 text-2xl font-semibold tracking-tight ${m.featured ? "text-white" : "text-slate-900"}`}>{m.name}</h3>
                <div className={`mt-1 text-sm ${m.featured ? "text-slate-400" : "text-slate-500"}`}>{m.duration} · {m.price}</div>
                <p className={`mt-4 text-[15px] leading-relaxed ${m.featured ? "text-slate-300" : "text-slate-600"}`}>{m.for}</p>
                <ul className={`mt-6 space-y-2 text-sm ${m.featured ? "text-slate-200" : "text-slate-700"}`}>
                  {m.includes.map((x, j) => (
                    <li key={j} className="flex gap-2">
                      <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${m.featured ? "bg-[#A78BFA]" : "bg-slate-900"}`} />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-slate-600 max-w-xl">Not sure where to start? Run the 5-minute Constraint Diagnostic — it will tell you which engagement fits.</p>
          <div className="flex gap-3">
            <Link to="/diagnostic" data-testid="services-diagnostic-cta" className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-sm font-medium px-5 py-3">
              Run Diagnostic <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" data-testid="services-contact-cta" className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-900 text-sm font-medium px-5 py-3 hover:bg-slate-50">
              Book working session
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
